import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../Components';
import { formatKWh, formatEuros, formatCelcius, CustomLegend, COLORS } from './ChartUtils';

const TotalHeatingEnergy = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  const processedData = useMemo(() => {
    if (!data?.heating_summary) return [];

    return Object.entries(data.heating_summary).map(([date, values]) => ({
      date,
      total_consumption: Number(values.total_consumption) || 0,
      outdoor_temp_avg: Number(values.outdoor_temp_avg) || 0
    }));
  }, [data]);
  
  
  const heatingInfo = [
    { label: "Lämmitysenergian kokonaiskulutus", value: formatKWh(data.metadata.heating_summary.total_consumption) },  // Assuming units are kWh, add if needed
    { label: "Lämmitysenergian kokonaishinta", value: formatEuros(data.metadata.heating_summary.total_cost) },
    { label: "Ulkolämpötilan keskiarvo", value: formatCelcius(data.metadata.heating_summary.outdoor_temp_avg_mtd) }
  ];

  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    // Initial update after a short delay to ensure chart is rendered
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  const handleMouseMove = (props) => {
    if (props.activePayload) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    setActiveData(processedData[processedData.length - 1]);
  };

  if (!processedData.length) return null;

  return (
    <div ref={chartRef} className="h-[400px]">
      <InfoBox title="Lämmitysenergian kulutus" dataPoints={heatingInfo} />
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={processedData}
          margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            yAxisId="left"
            label={{ value: 'Consumption', angle: -90, position: 'insideLeft' }}
          />
          <YAxis 
            yAxisId="right" 
            orientation="right"
            label={{ value: 'Temperature', angle: 90, position: 'insideRight' }}
          />
          <Tooltip 
            formatter={(value, name) => {
              if (name === 'Kokonaiskulutus') return formatKWh(value);
              if (name === 'Ulkolämpötila') return formatCelcius(value);
              return value;
            }}
          />
          <Bar 
            yAxisId="left"
            dataKey="total_consumption" 
            fill="#8884d8" 
            name="Kokonaiskulutus"
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="outdoor_temp_avg" 
            stroke="#ff7300" 
            name="Ulkolämpötila"
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      
      {containerWidth > 0 && (
        <div className="mt-4">
          <CustomLegend 
            payload={[
              { dataKey: 'total_consumption', name: 'Kokonaiskulutus' },
              { dataKey: 'outdoor_temp_avg', name: 'Ulkolämpötila' }
            ]}
            colors={['#8884d8', '#ff7300']}
            activeData={activeData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value, dataKey) => {
              if (dataKey === 'total_consumption') return formatKWh(value);
              if (dataKey === 'outdoor_temp_avg') return formatCelcius(value);
              return value ? `${value}` : '0';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TotalHeatingEnergy;
